import { useEffect, useRef, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Geolocation } from "@capacitor/geolocation";

import { StashtLocation } from "@/plugins/StashtLocation";
import { apiClient } from "@/lib/api/client";
import { LOCATION_PERMISSION_GRANTED_EVENT } from "@/hooks/locationPermissionEvents";

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

interface LocationPayload {
  lat: number;
  lng: number;
  accuracy_m?: number;
  source: "gps" | "network" | "home_fallback";
}

const hasExistingLocationGrant = async () => {
  try {
    if (Capacitor.isNativePlatform()) {
      const permissions = await Geolocation.checkPermissions();
      return permissions.location === "granted";
    }

    if (!navigator.permissions) return false;
    const permission = await navigator.permissions.query({ name: "geolocation" as PermissionName });
    return permission.state === "granted";
  } catch {
    return false;
  }
};

/**
 * Orchestrates periodic location collection and syncs it to the backend.
 *
 * - Native platforms: delegates to StashtLocation native plugin after location
 *   permission is already granted. The map flow owns the first OS prompt.
 * - Web: polls navigator.geolocation on a 5-min interval after permission exists.
 * - On every app resume (native) one fresh reading is requested after sync starts.
 *
 * Call this hook once, at the authenticated app root. Pass `enabled: false`
 * to pause (e.g. during logout).
 */
export function useLocationSync({ enabled = true }: { enabled?: boolean } = {}) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const listenerRef = useRef<Awaited<ReturnType<typeof StashtLocation.addListener>> | null>(null);
  const resumeListenerRef = useRef<Awaited<ReturnType<typeof App.addListener>> | null>(null);

  // ── post helper ──────────────────────────────────────────────────────────
  const post = useCallback(async (payload: LocationPayload) => {
    try {
      await apiClient.post("/location", payload);
    } catch {
      // Intentionally silent — location sync must never surface errors.
    }
  }, []);

  // ── single reading via browser / Capacitor geolocation ───────────────────
  const readOnce = useCallback(async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        const pos = await Geolocation.getCurrentPosition({
          enableHighAccuracy: false,
          timeout: 10_000,
        });
        await post({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
          source: "gps",
        });
      } else {
        // Web path
        if (!navigator.geolocation) return;
        const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: false,
            maximumAge: 5 * 60 * 1000,
            timeout: 10_000,
          });
        });
        await post({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
          source: "gps",
        });
      }
    } catch {
      // Swallow — permission denied, timeout, etc.
    }
  }, [post]);

  // ── lifecycle ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;

    const isNative = Capacitor.isNativePlatform();
    let mounted = true;
    let startState: "idle" | "starting" | "started" = "idle";

    const startSync = async ({ trustRecentGrant = false }: { trustRecentGrant?: boolean } = {}) => {
      if (startState !== "idle") return;
      startState = "starting";

      const canStart = trustRecentGrant || await hasExistingLocationGrant();
      if (!canStart || !mounted) {
        startState = "idle";
        return;
      }

      if (isNative) {
        const { started } = await StashtLocation.startUpdates();
        if (!started || !mounted) {
          startState = "idle";
          return;
        }

        listenerRef.current = await StashtLocation.addListener("locationUpdate", (data) => {
          if (!mounted) return;
          void post({
            lat: data.lat,
            lng: data.lng,
            accuracy_m: data.accuracy,
            source: "gps",
          });
        });

        resumeListenerRef.current = await App.addListener("appStateChange", ({ isActive }) => {
          if (isActive && mounted) void readOnce();
        });
      } else {
        void readOnce();
        intervalRef.current = setInterval(() => {
          if (mounted) void readOnce();
        }, POLL_INTERVAL_MS);
      }

      startState = "started";
    };

    const handleLocationGranted = () => {
      void startSync({ trustRecentGrant: true });
    };

    void startSync();
    window.addEventListener(LOCATION_PERMISSION_GRANTED_EVENT, handleLocationGranted);

    return () => {
      mounted = false;
      window.removeEventListener(LOCATION_PERMISSION_GRANTED_EVENT, handleLocationGranted);
      if (isNative) {
        void StashtLocation.stopUpdates();
        listenerRef.current?.remove();
        listenerRef.current = null;
        resumeListenerRef.current?.remove();
        resumeListenerRef.current = null;
      } else if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, post, readOnce]);
}
